import { useState } from 'react';
import { X, Search, ArrowUpDown, ChevronDown } from 'lucide-react';
import taricData from '../../../taric_tables_data.json';

let cachedDocumentos = null;
let documentosMapByCodigo = null;
let documentosMapByNombre = null;

// Utility to get documentos from taric_tables_data.json
export function getDocumentos() {
  if (cachedDocumentos) return cachedDocumentos;
  
  cachedDocumentos = (taricData['documentos'] || []).map((doc) => ({
    nombre: doc.descripcion,
    codigo: doc.codigo,
  }));
  
  documentosMapByCodigo = new Map();
  documentosMapByNombre = new Map();
  cachedDocumentos.forEach(doc => {
    if (doc.codigo) documentosMapByCodigo.set(doc.codigo, doc);
    if (doc.nombre) documentosMapByNombre.set(doc.nombre, doc);
  });
  
  return cachedDocumentos;
}

export function findDocumento(codigoOrNombre) {
  if (!codigoOrNombre) return null;
  if (!documentosMapByCodigo || !documentosMapByNombre) getDocumentos();
  return documentosMapByCodigo.get(codigoOrNombre) || documentosMapByNombre.get(codigoOrNombre) || null;
}

// Hardcoded tipos for documentosTipo
const documentosTipos = [
  { codigo: '1', nombre: 'Autorizaciones' },
  { codigo: '2', nombre: 'Documentos Suplementarios' },
  { codigo: '3', nombre: 'Referencias Adicionales' },
  { codigo: '4', nombre: 'Documentos de Transporte' },
];

let tiposMapByCodigo = null;

export function getDocumentosTipos() {
  return documentosTipos;
}

export function findDocumentoTipo(codigo) {
  if (!codigo) return null;
  if (!tiposMapByCodigo) {
    tiposMapByCodigo = new Map();
    documentosTipos.forEach(tipo => tiposMapByCodigo.set(tipo.codigo, tipo));
  }
  return tiposMapByCodigo.get(String(codigo)) || null;
}

export const DocumentosTipoModal = ({
  tipos = [],
  isOpen,
  onClose,
  onSelect,
  selectedTipo,
  title = 'Seleccionar Tipo de Documento',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { value: 'name-asc', label: 'Nombre (A-Z)' },
    { value: 'name-desc', label: 'Nombre (Z-A)' },
    { value: 'code-asc', label: 'Código (asc)' },
    { value: 'code-desc', label: 'Código (desc)' },
  ];

  const filteredTipos = (tipos.length ? tipos : documentosTipos).filter((tipo) =>
    tipo.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tipo.codigo.includes(searchQuery)
  );

  const sortedTipos = [...filteredTipos].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.nombre.localeCompare(b.nombre);
      case 'name-desc':
        return b.nombre.localeCompare(a.nombre);
      case 'code-asc':
        return a.codigo.localeCompare(b.codigo);
      case 'code-desc':
        return b.codigo.localeCompare(a.codigo);
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    return sortOptions.find((option) => option.value === sortBy)?.label || 'Ordenar por';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 animate-modalEnter">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            onClick={() => {
              setSearchQuery('');
              setIsSortOpen(false);
              onClose();
            }}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          {/* Search Input */}
          <div className="relative flex items-center gap-2 border-container rounded-full px-3 py-1 flex-1 flex-row">
            <div className="flex-0 pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="pr-4 py-2 !border-none no-border search-input"
              style={{ borderWidth: '0px !important', border: '0px !important' }}
              placeholder="Buscar tipo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Custom Sort Dropdown */}
          <div className="relative button-no-hover-effect text-xs">
            <button
              className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span>{getSortLabel()}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
            </button>

            {isSortOpen && (
              <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      sortBy === option.value ? 'text-primary' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="max-h-[60vh] min-h-[30vh] overflow-y-auto">
          {sortedTipos.length > 0 ? (
            <div className="space-y-0">
              {sortedTipos.map((tipo) => (
                <div
                  key={tipo.codigo}
                  className={`p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray ${
                    selectedTipo && selectedTipo.codigo === tipo.codigo ? 'bg-gray-100 font-bold' : ''
                  }`}
                  onClick={() => {
                    onSelect(tipo);
                    setSearchQuery('');
                    setIsSortOpen(false);
                    onClose();
                  }}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-medium">{tipo.nombre}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              No se encontraron tipos para "{searchQuery}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const DocumentosModal = ({
  documentos = [],
  isOpen,
  onClose,
  onSelect,
  selectedDocumento,
  title = 'Select Document Type',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
    { value: 'codigo-asc', label: 'Code (asc)' },
    { value: 'codigo-desc', label: 'Code (desc)' },
  ];

  const filteredDocumentos = documentos.filter((doc) =>
    doc.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (doc.codigo || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedDocumentos = [...filteredDocumentos].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.nombre.localeCompare(b.nombre);
      case 'name-desc':
        return b.nombre.localeCompare(a.nombre);
      case 'codigo-asc':
        return (a.codigo || '').localeCompare(b.codigo || '');
      case 'codigo-desc':
        return (b.codigo || '').localeCompare(a.codigo || '');
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    return sortOptions.find((option) => option.value === sortBy)?.label || 'Sort by';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 animate-modalEnter">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            onClick={() => {
              setSearchQuery('');
              setIsSortOpen(false);
              onClose();
            }}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          {/* Search Input */}
          <div className="relative flex items-center gap-2 border-container rounded-full px-3 py-1 flex-1 flex-row">
            <div className="flex-0 pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="pr-4 py-2 !border-none no-border search-input"
              style={{ borderWidth: '0px !important', border: '0px !important' }}
              placeholder="Search document types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Custom Sort Dropdown */}
          <div className="relative button-no-hover-effect text-xs">
            <button
              className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span>{getSortLabel()}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
            </button>

            {isSortOpen && (
              <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      sortBy === option.value ? 'text-primary' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
          {documentos.length > 0 ? (
            <div className="space-y-0">
              {sortedDocumentos.length > 0 ? (
                sortedDocumentos.map((doc) => (
                  <div
                    key={doc.codigo || doc.nombre}
                    className={`p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray ${
                      selectedDocumento &&
                      (selectedDocumento.codigo === doc.codigo || selectedDocumento.nombre === doc.nombre)
                        ? 'bg-gray-100 font-bold'
                        : ''
                    }`}
                    onClick={() => {
                      onSelect(doc);
                      setSearchQuery('');
                      setIsSortOpen(false);
                      onClose();
                    }}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <p className="font-medium">{doc.nombre}</p>
                        {doc.codigo && (
                          <p className="text-xs text-gray-500">{doc.codigo}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No document types found matching "{searchQuery}"
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className='opacity-75 mb-4'>No Document Types Available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 